import { useMemo } from "react";
import config from "../config";

const useScales = ({
  series,
  categories,
  padding,
  innerW,
  innerH,
  options = {},
}) => {
  const flat = useMemo(() => {
    const arr = [];
    series.forEach((s) => {
      if (Array.isArray(s?.data))
        s.data.forEach((v) => arr.push(typeof v === "number" ? v : 0));
    });
    return arr;
  }, [series]);

  const rawMinY = useMemo(() => {
    if (flat.length === 0) return 0;
    const m = Math.min(...flat);
    return m;
  }, [flat]);

  const rawMaxY = useMemo(() => {
    if (flat.length === 0) return 1;
    const m = Math.max(...flat);
    return m;
  }, [flat]);

  const xCount = categories?.length || series[0]?.data?.length || 0;
  const xStep = xCount > 1 ? innerW / (xCount - 1) : 0;

  const x = (i) => padding.left + i * xStep;

  const axisYOpt = {
    ...(config.axis?.y || {}),
    ...((options?.axis?.y ?? options?.yAxis) || {}),
  };
  const gridLinesOpt = axisYOpt?.gridLines ?? 5;
  const isPercentStack = (() => {
    const stacked =
      options?.chart?.stacked === true || options?.bar?.stacked === true;
    const type = String(
      options?.bar?.stackType ?? options?.chart?.stackType
    ).toLowerCase();
    return stacked && type === "100%";
  })();

  if (isPercentStack) {
    const segments = gridLinesOpt;
    const minY = 0;
    const maxY = 100;
    const step = 100 / segments;
    const y = (v) =>
      padding.top + innerH - ((v - minY) / (maxY - minY || 1)) * innerH;
    const yTicks = Array.from({ length: segments + 1 }, (_, i) => {
      const val = minY + i * step;
      const py = padding.top + innerH - (i * innerH) / segments;
      return { value: Math.round(val), y: py };
    });
    return { minY, maxY, xCount, x, y, yTicks };
  }

  const clampMinZero = axisYOpt?.clampMinZero ?? true;
  const baseMin = clampMinZero && rawMinY > 0 ? 0 : Math.floor(rawMinY);
  const range = Math.max(1, rawMaxY - baseMin);
  const stepUnrounded = range / gridLinesOpt;
  const niceNumber = (value) => {
    const exp = Math.floor(Math.log10(value));
    const f = value / Math.pow(10, exp);
    let nf = 1;
    if (f <= 1.5) nf = 1;
    else if (f <= 3) nf = 2;
    else if (f <= 7) nf = 5;
    else nf = 10;
    return nf * Math.pow(10, exp);
  };
  const stepOverride = axisYOpt?.step;
  const step =
    typeof stepOverride === "number" && stepOverride > 0
      ? stepOverride
      : niceNumber(stepUnrounded);
  const niceMin = Math.floor(baseMin / step) * step;

  const segments = Math.max(1, Math.ceil((rawMaxY - niceMin) / step));
  const maxY = niceMin + segments * step;
  const minY = niceMin;

  const y = (v) =>
    padding.top + innerH - ((v - minY) / (maxY - minY || 1)) * innerH;

  const yTicks = Array.from({ length: segments + 1 }, (_, i) => {
    const val = minY + i * step;
    const py = padding.top + innerH - (i * innerH) / segments;
    const showInt = step >= 1;
    return {
      value: showInt ? Math.round(val) : Math.round(val * 100) / 100,
      y: py,
    };
  });

  return { minY, maxY, xCount, x, y, yTicks };
};

export default useScales;
